/**
 * Session Tracking Utility
 * Tracks user session duration and sends events to Google Analytics
 */

import { trackEvent, setUserProperties } from './analytics';

const SESSION_START_KEY = 'analytics_session_start';
const SESSION_ID_KEY = 'analytics_session_id';

interface SessionData {
  startTime: number;
  sessionId: string;
  pageViews: number;
  events: number;
  featuresUsed: Set<string>;
}

/**
 * Initialize or resume a session
 */
export const startSession = (): void => {
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const sessionId = generateSessionId();
  
  const sessionData: SessionData = {
    startTime: now,
    sessionId,
    pageViews: 0,
    events: 0,
    featuresUsed: new Set(),
  };

  try {
    sessionStorage.setItem(SESSION_START_KEY, now.toString());
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    
    // Track session start
    trackEvent('session_start', {
      session_id: sessionId,
    });
  } catch (error) {
    console.warn('Failed to start session tracking:', error);
  }
};

/**
 * End session and track duration
 */
export const endSession = (): void => {
  if (typeof window === 'undefined') return;

  try {
    const startTimeStr = sessionStorage.getItem(SESSION_START_KEY);
    if (!startTimeStr) return;

    const startTime = parseInt(startTimeStr, 10);
    const sessionId = sessionStorage.getItem(SESSION_ID_KEY) || 'unknown';
    const duration = Math.floor((Date.now() - startTime) / 1000); // Duration in seconds

    // Track session end with duration
    trackEvent('session_end', {
      session_id: sessionId,
      session_duration: duration,
      duration_seconds: duration,
    });

    // Clear session data
    sessionStorage.removeItem(SESSION_START_KEY);
    sessionStorage.removeItem(SESSION_ID_KEY);
  } catch (error) {
    console.warn('Failed to end session tracking:', error);
  }
};

/**
 * Track page view within session
 */
export const trackSessionPageView = (): void => {
  if (typeof window === 'undefined') return;

  try {
    const startTimeStr = sessionStorage.getItem(SESSION_START_KEY);
    if (!startTimeStr) {
      // No session started, initialize one
      startSession();
      return;
    }

    // Increment page view count (stored separately for easy access)
    const pageViews = getPageViewCount() + 1;
    sessionStorage.setItem('analytics_page_views', pageViews.toString());
  } catch (error) {
    console.warn('Failed to track session page view:', error);
  }
};

/**
 * Get current session duration in seconds
 */
export const getSessionDuration = (): number => {
  if (typeof window === 'undefined') return 0;

  try {
    const startTimeStr = sessionStorage.getItem(SESSION_START_KEY);
    if (!startTimeStr) return 0;

    const startTime = parseInt(startTimeStr, 10);
    return Math.floor((Date.now() - startTime) / 1000);
  } catch (error) {
    return 0;
  }
};

/**
 * Get page view count for current session
 */
export const getPageViewCount = (): number => {
  if (typeof window === 'undefined') return 0;

  try {
    const count = sessionStorage.getItem('analytics_page_views');
    return count ? parseInt(count, 10) : 0;
  } catch (error) {
    return 0;
  }
};

/**
 * Track feature usage in session
 */
export const trackFeatureUsage = (featureName: string): void => {
  if (typeof window === 'undefined') return;

  try {
    const featuresStr = sessionStorage.getItem('analytics_features_used');
    const features = featuresStr ? new Set(JSON.parse(featuresStr)) : new Set<string>();
    
    if (!features.has(featureName)) {
      features.add(featureName);
      sessionStorage.setItem('analytics_features_used', JSON.stringify([...features]));
    }
  } catch (error) {
    console.warn('Failed to track feature usage:', error);
  }
};

/**
 * Get features used in current session
 */
export const getFeaturesUsed = (): string[] => {
  if (typeof window === 'undefined') return [];

  try {
    const featuresStr = sessionStorage.getItem('analytics_features_used');
    return featuresStr ? JSON.parse(featuresStr) : [];
  } catch (error) {
    return [];
  }
};

/**
 * Generate a unique session ID
 */
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Initialize session tracking on page load
 * This runs after DOM is ready to avoid any initialization issues
 */
if (typeof window !== 'undefined') {
  // Use DOMContentLoaded to ensure DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Start session if not already started
      if (!sessionStorage.getItem(SESSION_START_KEY)) {
        startSession();
      }
    });
  } else {
    // DOM already loaded
    if (!sessionStorage.getItem(SESSION_START_KEY)) {
      startSession();
    }
  }

  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    endSession();
  });

  // Track page visibility changes (user switches tabs/apps)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      // Page hidden - could track as session pause
      // For now, we'll just let the session continue
    } else {
      // Page visible again - session continues
    }
  });
}

